import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { useAuth } from "@/context/AuthContext";
import { confirmDialog } from "@/components/Confirm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Lock, Plus, Trash2, Settings2, Phone, User, History, Undo2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Er ging iets mis");

function VaultSettings({ items, reload }) {
  const [name, setName] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post("/vault", { name }); setName(""); toast.success("Item toegevoegd aan kluis"); reload(); } catch (e2) { err(e2); }
  };
  const remove = async (it) => {
    if (!(await confirmDialog({ title: "Item verwijderen", message: `"${it.name}" uit de kluis verwijderen? De geschiedenis wordt ook verwijderd.`, danger: true, confirmText: "Verwijderen" }))) return;
    try { await api.delete(`/vault/${it.id}`); reload(); } catch (e2) { err(e2); }
  };
  return (
    <div data-testid="vault-settings" className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-3 text-sm font-bold text-slate-900">Instellingen — items in de kluis</div>
      <form onSubmit={add} className="mb-4 flex gap-2">
        <input data-testid="vault-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bijv. Masterkey 1"
          className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
        <button data-testid="vault-add-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> Toevoegen</button>
      </form>
      <div className="flex flex-wrap gap-2">
        {items.length === 0 && <div className="text-sm text-slate-400">Nog geen items.</div>}
        {items.map((it) => (
          <span key={it.id} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1 text-sm text-slate-700">
            {it.name}
            <button data-testid={`vault-delete-${it.id}`} onClick={() => remove(it)} title="Verwijderen" className="rounded-full p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function IssueDialog({ item, onClose, reload }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(""); setPhone(""); }, [item]);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Vul een naam in"); return; }
    setBusy(true);
    try { await api.post(`/vault/${item.id}/issue`, { name, phone }); toast.success(`${item.name} uitgegeven aan ${name}`); onClose(); reload(); } catch (e2) { err(e2); }
    setBusy(false);
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="vault-issue-dialog">
        <DialogHeader><DialogTitle>{item?.name} uitgeven</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-sm font-medium text-slate-700">Naam
            <input data-testid="vault-issue-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
          </label>
          <label className="block text-sm font-medium text-slate-700">Telefoonnummer <span className="font-normal text-slate-400">(optioneel)</span>            <input data-testid="vault-issue-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
          </label>
          <button data-testid="vault-issue-submit" disabled={busy} className="w-full rounded-xl bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60">Uitgeven</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ item, onClose, reload }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/vault/${item.id}/return`); toast.success(`${item.name} retour gemeld`); onClose(); reload(); } catch (e2) { err(e2); }
    setBusy(false);
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="vault-return-dialog">
        <DialogHeader><DialogTitle>{item?.name} is uitgegeven</DialogTitle></DialogHeader>
        {item && (
          <div className="space-y-2 rounded-xl bg-red-50 p-4 text-sm text-slate-800">
            <div className="flex items-center gap-2"><User className="h-4 w-4 text-red-600" /> <span data-testid="vault-return-name" className="font-semibold">{item.issued_to}</span></div>
            {item.issued_phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-red-600" /> <a data-testid="vault-return-phone" href={`tel:${item.issued_phone}`} className="underline">{item.issued_phone}</a></div>}
            <div className="text-xs text-slate-500">Uitgegeven op {fmt(item.issued_at)}{item.issued_by ? ` door ${item.issued_by}` : ""}</div>
          </div>
        )}
        <button data-testid="vault-return-submit" onClick={submit} disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"><Undo2 className="h-4 w-4" /> Retour melden</button>
      </DialogContent>
    </Dialog>
  );
}

function HistoryDialog({ item, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (item) { api.get(`/vault/${item.id}/history`).then(({ data }) => setRows(data)).catch(() => setRows([])); } }, [item]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="vault-history-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Geschiedenis — {item?.name}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {rows.length === 0 && <div className="py-6 text-center text-sm text-slate-400">Nog niet uitgegeven geweest.</div>}
          {rows.map((r) => (
            <div key={r.id} data-testid="vault-history-row" className="rounded-xl border border-slate-200 p-3 text-sm">
              <div className="flex items-center justify-between"><span className="font-semibold text-slate-900">{r.to_name}</span><span className="text-slate-500">{r.to_phone || ""}</span></div>
              <div className="mt-1 text-xs text-slate-500">Uit: {fmt(r.issued_at)}{r.issued_by ? ` (${r.issued_by})` : ""}</div>
              <div className={`text-xs ${r.returned_at ? "text-emerald-700" : "font-semibold text-red-600"}`}>{r.returned_at ? `Retour: ${fmt(r.returned_at)}${r.returned_by ? ` (${r.returned_by})` : ""}` : "Nog niet retour"}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VaultTile({ it, onClick, onHistory }) {
  return (
    <div className="relative">
      <button data-testid={`vault-item-${it.id}`} onClick={onClick}
        className={`flex h-40 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 p-4 text-center shadow-sm transition-transform hover:-translate-y-0.5 ${it.issued ? "border-red-600 bg-red-600 text-white shadow-red-600/30" : "border-slate-200 bg-white text-slate-900 hover:border-blue-400"}`}>
        <KeyRound className={`h-8 w-8 ${it.issued ? "text-white" : "text-blue-600"}`} />
        <div className="text-lg font-bold">{it.name}</div>
        <div data-testid={`vault-item-status-${it.id}`} className={`text-xs font-semibold ${it.issued ? "text-red-100" : "text-emerald-600"}`}>{it.issued ? `Uitgegeven aan ${it.issued_to}` : "In de kluis"}</div>
      </button>
      <button data-testid={`vault-history-${it.id}`} onClick={onHistory} title="Geschiedenis"
        className={`absolute right-2 top-2 rounded-lg p-1.5 ${it.issued ? "text-red-100 hover:bg-red-500" : "text-slate-400 hover:bg-slate-100"}`}><History className="h-4 w-4" /></button>
    </div>
  );
}

export default function Vault() {
  const { user } = useAuth();
  const isAdmin = user?.role === "beheerder";
  const [items, setItems] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [issueItem, setIssueItem] = useState(null);
  const [returnItem, setReturnItem] = useState(null);
  const [histItem, setHistItem] = useState(null);
  const load = useCallback(() => api.get("/vault").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useRealtime((m) => { if (m?.type === "vault_updated" || m?.type === "__ws_open") load(); });
  const issuedCount = items.filter((i) => i.issued).length;
  return (
    <div data-testid="vault-page" className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900"><Lock className="h-6 w-6 text-blue-600" /> Kluis</h1>
          <p data-testid="vault-summary" className="text-sm text-slate-500">{items.length} items · {issuedCount} uitgegeven</p>
        </div>
        {isAdmin && (
          <button data-testid="vault-settings-toggle" onClick={() => setShowSettings((s) => !s)}
            className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold ${showSettings ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}><Settings2 className="h-4 w-4" /> Instellingen</button>
        )}
      </div>
      {isAdmin && showSettings && <VaultSettings items={items} reload={load} />}
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400">De kluis is leeg.{isAdmin ? " Voeg items toe via Instellingen." : ""}</div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((it) => <VaultTile key={it.id} it={it} onClick={() => (it.issued ? setReturnItem(it) : setIssueItem(it))} onHistory={() => setHistItem(it)} />)}
        </div>
      )}
      <IssueDialog item={issueItem} onClose={() => setIssueItem(null)} reload={load} />
      <ReturnDialog item={returnItem} onClose={() => setReturnItem(null)} reload={load} />
      <HistoryDialog item={histItem} onClose={() => setHistItem(null)} />
    </div>
  );
}
